import { memo, useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import {
  backUrlVar,
  selectFrequencyChoiceVar,
  snackbarInfo,
} from 'store/utils';
import { getDecimalPricePerMeal } from '@utils/helpers';
import Controls from '@components/Controls';
import InputError from '@components/InputError';
import { AUTO_PROMO_CODE } from '@utils/data';
import {
  updatePromoCode,
  usePersistentPromoCode,
  usePersistentPromoCodeDetails,
} from 'store/promoCode';
import { useGetPromoCodeDetailsMutation } from 'commonModules/account';
import useAuth from '@utils/useAuth';
import { useReactiveVar } from '@apollo/client';

import { useStyles } from './useStyles';
import Accordion from '@components/Accordian';
import { usePersistentCartItems } from 'store/cartItems';
import { productFlatListVar } from 'store/productsFlatList';
import { productType } from '@utils/types';

interface Reviewprops {
  onClickApply: (promoCode: any) => void;
  onClickPromoCodeErrorCloseIcon: () => void;
  orderTotalData: any;
  setOrderTotalData: any;
  loading?: boolean;
  showNoMeal?: boolean;
  title?: string;
  emailAdd: string;
  setOrderTotalDataWithPromoCode: any;
  getPromoDetail: any;
}

export const getDish = ({
  product,
  value,
  isFreeTextToShow,
}: {
  product: productType;
  value?: number;
  isFreeTextToShow?: boolean;
}) => (
  <>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        columnGap: '16px',
      }}
    >
      <Box position="relative" width="60px" height="60px">
        {product?.image_url ? (
          <Image
            src={product?.image_url}
            alt={product?.title}
            objectFit="contain"
            layout="fill"
          />
        ) : null}
      </Box>
      <Typography variant="body2" align="left" color="garamMasala.main">
        {product?.title}
      </Typography>
    </Box>
    <Typography
      variant="body1"
      color="garamMasala.main"
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      {isFreeTextToShow ? (
        'FREE'
      ) : (
        <>
          <CloseIcon fontSize="small" sx={{ color: 'garamMasala.main' }} />
          {value}
        </>
      )}
    </Typography>
  </>
);

const Review = ({
  onClickApply,
  onClickPromoCodeErrorCloseIcon,
  orderTotalData,
  setOrderTotalData,
  loading,
  showNoMeal = false,
  title,
  emailAdd,
  setOrderTotalDataWithPromoCode,
  getPromoDetail,
}: Reviewprops) => {
  const [expanded, setExpanded] = useState<{
    panel1: boolean;
    panel2: boolean;
    panel3: boolean;
  }>({ panel1: false, panel2: false, panel3: false });
  const [promoCode, setPromoCode] = useState('');

  const router = useRouter();
  const { TRAIL_PLAN } = router?.query;

  const classes = useStyles();
  const { orderTotal } = orderTotalData || {};
  const {
    promoDetails,
    nonSidesQuantity,
    sidesQuantity,
    nonSidesPrice,
    sidesPrice,
    shippingCost,
    salesTax,
    appliedCuminCredits,
    totalPrice,
  } = orderTotal || {};

  const { authState } = useAuth();
  const cartItems = usePersistentCartItems();
  const { productFlatList } = useReactiveVar(productFlatListVar);

  const promoCodeValue = usePersistentPromoCode();

  const promoCodeDetails = usePersistentPromoCodeDetails();

  const backUrl = useReactiveVar(backUrlVar); // step-3 from redirect page

  // for user come with promo query on landing page
  useEffect(() => {
    if (promoCodeDetails?.isPromoValid && promoCodeValue) {
      const email = authState?.user?.email || emailAdd;

      if (
        !orderTotalData?.isPromoRemove &&
        !orderTotal?.isEmailApplied &&
        !promoDetails?.isPromoValid &&
        !email
      ) {
        setPromoCode(promoCodeValue);
        setOrderTotalDataWithPromoCode({
          promoDetail: promoCodeDetails,
          newOrderTotalData: orderTotalData?.orderTotal,
        });
      }
    }
  }, [promoCodeDetails?.isPromoValid && promoCodeValue, orderTotalData]);

  const onClickApplyPromo = (promoCode: any) => {
    const email = authState?.user?.email || emailAdd;

    if (promoCode !== null && !promoCode) {
      snackbarInfo({
        message: 'Please enter the promo code',
        type: 'error',
      });
    } else if (
      promoDetails?.promoCode === AUTO_PROMO_CODE ||
      (!email && promoCode === null)
    ) {
      const orderData = {
        orderTotal: {
          ...orderTotalData?.orderTotal,
          orderTotal: totalPrice,
          promoDetails: null,
        },
      };
      updatePromoCode('');
      setOrderTotalData(orderData);
    } else {
      onClickApply(promoCode);
    }
  };

  const onChangePromo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPromoCode(value);
  };

  useEffect(() => {
    if (promoCodeValue === null) {
      setPromoCode('');
    }
  }, [promoCodeValue]);

  const handleChange = (expand: boolean, panelName: string) => {
    setExpanded({ ...expanded, [panelName]: expand });
  };

  return (
    <>
      <Box className={classes.mainContainer}>
        <Typography variant="h3" color="cinnamon.main" mb={{ xs: 2, sm: 4 }}>
          {title || 'Review'}
        </Typography>

        {/*Meals*/}
        <Box className={classes.flexSpaceBtw}>
          {showNoMeal ? (
            <>
              <>
                <Typography component="span" variant="body2" color="cumin.main">
                  Meals Subtotal ({nonSidesQuantity})
                </Typography>{' '}
                <Typography component="span" variant="body2" color="cumin.main">
                  {orderTotal?.currencySymbol +
                    '' +
                    getDecimalPricePerMeal(nonSidesPrice).toFixed(2)}
                </Typography>
              </>
            </>
          ) : (
            <Accordion
              bgcolor="#FFF2E8"
              sx={{ width: '100%' }}
              onChange={(_event: React.SyntheticEvent, expanded: boolean) =>
                handleChange(expanded, 'panel1')
              }
              expanded={expanded.panel1}
            >
              <AccordionSummary
                aria-controls="panel1a-content"
                sx={{ padding: 0, margin: '-12px 0' }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      columnGap: '12px',
                    }}
                  >
                    <Typography variant="body2" color="cumin.main">
                      Meals Subtotal ({nonSidesQuantity})
                    </Typography>
                    <KeyboardArrowDownIcon
                      fontSize="medium"
                      sx={{
                        color: 'cumin.main',
                        transform: expanded.panel1 ? 'rotate(180deg)' : '',
                      }}
                    />
                  </Box>

                  <Typography
                    component="span"
                    variant="body2"
                    color="cumin.main"
                  >
                    {orderTotal?.currencySymbol +
                      '' +
                      getDecimalPricePerMeal(nonSidesPrice).toFixed(2)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '8px 0 16px' }}>
                <Box className={classes.dishWrapper}>
                  {Object.entries(cartItems)?.map(([key, value]: any) => {
                    if (
                      productFlatList[+key]?.isActive &&
                      !productFlatList[+key]?.unitsPerSide
                    ) {
                      const product = productFlatList[+key];
                      return (
                        <Box className={classes.dish} key={key}>
                          {getDish({ product, value })}
                        </Box>
                      );
                    }
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>

        {/*sides*/}
        <Box className={classes.flexSpaceBtw}>
          {sidesQuantity && !showNoMeal ? (
            <Accordion
              bgcolor="#FFF2E8"
              sx={{ width: '100%' }}
              expanded={expanded.panel2}
              onChange={(_event: React.SyntheticEvent, expanded: boolean) =>
                handleChange(expanded, 'panel2')
              }
            >
              <AccordionSummary
                aria-controls="panel1a-content"
                sx={{ padding: 0, margin: '-12px 0' }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      columnGap: '12px',
                    }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      color="cumin.main"
                    >
                      Sides Subtotal ({sidesQuantity})
                    </Typography>
                    <KeyboardArrowDownIcon
                      fontSize="medium"
                      sx={{
                        color: 'cumin.main',
                        transform: expanded.panel2 ? 'rotate(180deg)' : '',
                      }}
                    />
                  </Box>
                  <Typography
                    component="span"
                    variant="body2"
                    color="cumin.main"
                  >
                    {orderTotal?.currencySymbol +
                      '' +
                      getDecimalPricePerMeal(sidesPrice).toFixed(2)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '8px 0 16px' }}>
                <Box className={classes.dishWrapper}>
                  {Object.entries(cartItems)?.map(([key, value]: any) => {
                    if (
                      productFlatList[+key]?.isActive &&
                      productFlatList[+key]?.unitsPerSide
                    ) {
                      const product = productFlatList[+key];
                      return (
                        <Box className={classes.dish} key={key}>
                          {getDish({ product, value })}
                        </Box>
                      );
                    }
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          ) : (
            <>
              <Typography component="span" variant="body2" color="cumin.main">
                Sides Subtotal ({sidesQuantity})
              </Typography>{' '}
              <Typography component="span" variant="body2" color="cumin.main">
                {orderTotal?.currencySymbol +
                  '' +
                  getDecimalPricePerMeal(sidesPrice).toFixed(2)}
              </Typography>
            </>
          )}
        </Box>

        {/*Free Meals Promo Product */}
        {promoDetails?.isPromoValid &&
        (promoDetails?.amountType === 'FREE_MEALS' ||
          promoDetails?.amountType === 'FREE_GIFTS') ? (
          <Box className={classes.flexSpaceBtw}>
            {showNoMeal ? (
              <>
                <Typography component="span" variant="body2" color="cumin.main">
                  {promoDetails?.amountType === 'FREE_MEALS'
                    ? 'Free Meals'
                    : 'Welcome Gift'}{' '}
                  ({promoDetails?.products?.length})
                </Typography>{' '}
                <Typography component="span" variant="body2" color="cumin.main">
                  {orderTotal?.currencySymbol + '' + '0.00'}
                </Typography>
              </>
            ) : (
              <Accordion
                bgcolor="#FFF2E8"
                sx={{ width: '100%' }}
                onChange={(_event: React.SyntheticEvent, expanded: boolean) =>
                  handleChange(expanded, 'panel3')
                }
                expanded={expanded.panel3}
              >
                <AccordionSummary
                  aria-controls="panel1a-content"
                  sx={{ padding: 0, margin: '-12px 0' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        columnGap: '12px',
                      }}
                    >
                      <Typography variant="body2" color="cumin.main">
                        {promoDetails?.amountType === 'FREE_MEALS'
                          ? 'Free Meals'
                          : 'Welcome Gift'}{' '}
                        ({promoDetails?.products?.length})
                      </Typography>
                      <KeyboardArrowDownIcon
                        fontSize="medium"
                        sx={{
                          color: 'cumin.main',
                          transform: expanded.panel3 ? 'rotate(180deg)' : '',
                        }}
                      />
                    </Box>

                    <Typography
                      component="span"
                      variant="body2"
                      color="cumin.main"
                    >
                      {orderTotal?.currencySymbol + '' + '0.00'}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '8px 0 16px' }}>
                  <Box className={classes.dishWrapper}>
                    {promoDetails?.products?.map((product: any) => {
                      return (
                        <Box className={classes.dish} key={product?.id}>
                          {getDish({ product, value: product?.quantity })}
                        </Box>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        ) : null}

        {salesTax ? (
          <Box className={classes.flexSpaceBtw}>
            <Typography component="span" variant="body2" color="cumin.main">
              Sales Tax
            </Typography>
            <Typography component="span" variant="body2" color="cumin.main">
              {orderTotal?.currencySymbol +
                '' +
                getDecimalPricePerMeal(salesTax as number).toFixed(2)}
            </Typography>
          </Box>
        ) : null}

        <Box className={classes.flexSpaceBtw}>
          <Typography component="span" variant="body2" color="cumin.main">
            Shipping
          </Typography>
          <Typography component="span" variant="body2" color="cumin.main">
            {shippingCost
              ? orderTotal?.currencySymbol +
                '' +
                getDecimalPricePerMeal(shippingCost).toFixed(2)
              : 'FREE'}
          </Typography>
        </Box>

        {appliedCuminCredits ? (
          <Box className={classes.flexSpaceBtw}>
            <Typography component="span" variant="body2" color="cumin.main">
              Cumin credits
            </Typography>
            <Typography component="span" variant="body2" color="cumin.main">
              {orderTotal?.currencySymbol +
                '' +
                getDecimalPricePerMeal(appliedCuminCredits).toFixed(2)}
            </Typography>
          </Box>
        ) : null}

        {TRAIL_PLAN ? null : promoDetails?.isPromoValid ? (
          <>
            {promoDetails?.amountType === 'FREE_MEALS' ||
            promoDetails?.amountType === 'FREE_GIFTS' ? null : (
              <Box className={classes.flexSpaceBtw}>
                <Typography component="span" variant="body2" color="cumin.main">
                  Promo Discount
                </Typography>

                <Typography component="span" variant="body2" color="cumin.main">
                  {'- ' +
                    orderTotal?.currencySymbol +
                    '' +
                    getDecimalPricePerMeal(
                      promoDetails?.discountAmount || 0
                    ).toFixed(2)}
                </Typography>
              </Box>
            )}
            <Controls.promoInput
              placeholder="Promo code"
              value={promoDetails?.promoCode}
              onChange={onChangePromo}
              onClickApply={() => {
                onClickApplyPromo(null);
              }}
              isPromoApplied={true}
              loading={loading}
            />
          </>
        ) : (
          <Box className={classes.promoCodeBtnWrapper}>
            <Controls.promoInput
              placeholder="Promo code"
              value={promoCode}
              onChange={onChangePromo}
              onClickApply={() => {
                onClickApplyPromo(promoCode);
              }}
              loading={loading}
            />
            <InputError
              error={promoDetails?.message}
              className={classes.promoCodeError}
              onClickErrorCloseIcon={onClickPromoCodeErrorCloseIcon}
            />
          </Box>
        )}

        <Divider
          sx={{
            height: '1px',
            backgroundColor: 'cinnamon.main',
            margin: { xs: '20px 0', md: '35px 0' },
          }}
        />
        <Box className={classes.flexSpaceBtw}>
          <Typography
            component="span"
            variant="body2"
            color="cinnamon.main"
            sx={{ fontWeight: 700 }}
          >
            Order Total
          </Typography>
          <Typography
            component="span"
            variant="body2"
            color="cinnamon.main"
            sx={{ fontWeight: 700 }}
          >
            {orderTotal?.currencySymbol +
              '' +
              getDecimalPricePerMeal(orderTotal?.orderTotal).toFixed(2)}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default memo(Review);
